import type sv from './sv'

type DeepString<T> = T extends string
  ? string
  : { readonly [K in keyof T]: DeepString<T[K]> }

const fi: DeepString<typeof sv> = {
  title: 'Luo sovelluksesi',
  description:
    'Rakenna sovelluksesi Convex SaaS:n päälle, tutustu dokumentaatioon ja aloita matkasi.',
  nav: {
    dashboard: 'Kojelauta',
    jobs: 'Työpaikat',
    candidates: 'Hakijat',
    questionBank: 'Kysymyspankki',
    templates: 'Mallipohjat',
    settings: 'Asetukset',
    billing: 'Laskutus',
    documentation: 'Dokumentaatio',
    personalAccount: 'Henkilökohtainen tili',
    topUpBalance: 'Lisää saldoa',
    theme: 'Teema',
    language: 'Kieli',
    logOut: 'Kirjaudu ulos',
  },
  header: {
    dashboard: {
      title: 'Kojelauta',
      description: 'Hallinnoi sovelluksiasi ja tarkastele käyttöäsi.',
    },
    jobs: {
      title: 'Työpaikat',
      description: 'Hallinnoi työpaikkailmoituksiasi ja haastattelumalleja.',
    },
    settings: {
      title: 'Asetukset',
      description: 'Hallinnoi tiliasetuksiasi.',
    },
    billing: {
      title: 'Laskutus',
      description: 'Hallinnoi saldoasi ja osta haastatteluja.',
    },
    organisation: {
      title: 'Organisaatio',
      description: 'Hallinnoi organisaatioasetuksia.',
    },
    team: {
      title: 'Tiimi',
      description: 'Hallinnoi tiimin jäseniä ja käyttöoikeuksia.',
    },
    interviews: {
      title: 'Haastatteluraportti',
      description:
        'Tarkastele hakijan haastattelutuloksia ja tekoälyanalyysiä.',
    },
    questionBank: {
      title: 'Kysymyspankki',
      description: 'Tallenna ja käytä haastattelukysymyksiä eri työpaikkoihin.',
    },
    templates: {
      title: 'Työmalllit',
      description: 'Tallenna ja käytä työkonfiguraatioita mallipohjina.',
    },
    candidates: {
      title: 'Hakijat',
      description: 'Hae ja suodata hakijoita kaikista työpaikoista.',
    },
  },
  auth: {
    login: {
      title: 'Jatka Convex SaaS:iin',
      subtitle: 'Tervetuloa takaisin! Kirjaudu sisään jatkaaksesi.',
      emailLabel: 'Sähköposti',
      emailPlaceholder: 'Sähköposti',
      emailInvalid: 'Sähköpostiosoite ei ole kelvollinen.',
      continueWithEmail: 'Jatka sähköpostilla',
      termsText: 'Napsauttamalla jatka hyväksyt',
      termsOfService: 'Käyttöehdot',
      and: 'ja',
      privacyPolicy: 'Tietosuojakäytännön.',
      ssoSignIn: 'Kirjaudu SSO:lla',
      ssoOr: 'tai',
      ssoRedirecting: 'Ohjataan identiteettipalveluntarjoajallesi…',
      ssoNotConfigured:
        'SSO:ta ei ole määritetty tälle sähköpostitoimialueelle.',
    },
    verify: {
      title: 'Tarkista postilaatikkosi!',
      subtitle:
        'Lähetimme juuri väliaikaisen salasanan sähköpostiisi. Syötä se alle.',
      codePlaceholder: 'Koodi',
      codeLabel: 'Koodi',
      codeMinLength: 'Koodin on oltava vähintään 8 merkkiä.',
      continue: 'Jatka',
      didNotReceive: 'Etkö saanut koodia?',
      requestNewCode: 'Pyydä uusi koodi',
      codeSent: 'Uusi koodi on lähetetty.',
      invalidCode: 'Virheellinen tai vanhentunut koodi.',
    },
  },
  onboarding: {
    username: {
      title: 'Tervetuloa!',
      subtitle: 'Aloitetaan profiilisi määrittämisestä.',
      organizationNamePlaceholder: 'Organisaation nimi',
      organizationNameLabel: 'Organisaation nimi',
      firstNamePlaceholder: 'Etunimi',
      firstNameLabel: 'Etunimi',
      lastNamePlaceholder: 'Sukunimi',
      lastNameLabel: 'Sukunimi',
      usernamePlaceholder: 'Käyttäjänimi',
      usernameLabel: 'Käyttäjänimi',
      continue: 'Jatka',
      hint: 'Voit päivittää käyttäjänimesi milloin tahansa tiliasetuksista.',
    },
  },
  jobs: {
    createJob: 'Luo työpaikka',
    noJobs: 'Ei työpaikkoja vielä',
    noJobsDescription: 'Luo ensimmäinen työpaikkasi aloittaaksesi.',
    statusDraft: 'Luonnos',
    statusActive: 'Aktiivinen',
    statusPaused: 'Tauotettu',
    duplicate: 'Monista',
    pause: 'Tauko',
    pauseConfirm: 'Oletko varma?',
    delete: 'Poista',
    deleteConfirm: 'Oletko varma?',
    form: {
      titleLabel: 'Otsikko',
      titlePlaceholder: 'Esim. Backend-kehittäjä',
      descriptionLabel: 'Kuvaus',
      descriptionPlaceholder: 'Kuvaile roolia ja mitä etsitte...',
      languageLabel: 'Haastattelukieli',
      languageSv: 'Ruotsi',
      languageEn: 'Englanti',
      languageNo: 'Norja',
      languageDa: 'Tanska',
      languageFi: 'Suomi',
      languageBoth: 'Ruotsi & Englanti',
      allowedModalitiesLabel: 'Sallitut modaliteetit',
      videoLabel: 'Video',
      videoRequired: 'Pakollinen',
      videoOptional: 'Valinnainen',
      videoDisabled: 'Poistettu käytöstä',
      audioLabel: 'Ääni',
      audioRequired: 'Pakollinen',
      audioDisabled: 'Poistettu käytöstä',
      chatLabel: 'Chat',
      chatEnabled: 'Käytössä',
      chatDisabled: 'Poistettu käytöstä',
      expiryDaysLabel: 'Linkin voimassaoloaika (päiviä)',
      interviewCountLabel: 'Haastattelujen määrä',
      topNLabel: 'Esiteltävien top-hakijoiden määrä',
      save: 'Tallenna',
      create: 'Luo työpaikka',
      cancel: 'Peruuta',
    },
    tabs: {
      overview: 'Yleiskatsaus',
      questions: 'Kysymykset',
      screener: 'Esikarsinta',
      additionalInfo: 'Lisätiedot',
      modality: 'Modaliteetti',
      voice: 'Ääni',
      candidates: 'Hakijat',
      candidateActions: 'Toiminnot',
      report: 'Raportti',
      settings: 'Asetukset',
    },
    overview: {
      name: 'Nimi',
      email: 'Sähköposti',
      status: 'Tila',
      score: 'Pisteet',
      report: 'Raportti',
      noScore: '—',
      viewReport: 'Näytä raportti',
      shortlist: 'Lyhyt lista',
      shortlistedOnly: 'Vain lyhyellä listalla',
      shareShortlist: 'Jaa lyhyt lista',
      copyLink: 'Kopioi linkki',
      linkCopied: 'Linkki kopioitu!',
    },
    shortlist: {
      title: 'Lyhyellä listalla olevat hakijat',
      jobLabel: 'Työpaikka',
      name: 'Nimi',
      status: 'Tila',
      score: 'Pisteet',
      noScore: '—',
      empty: 'Yhtään hakijaa ei ole vielä lyhyellä listalla.',
    },
    launch: {
      button: 'Julkaise työpaikka',
      topUpAndLaunch: 'Lisää saldoa ja julkaise',
      active: 'Aktiivinen',
      insufficientBalance:
        'Tarvitset vähintään {{required}} € julkaistaksesi. Nykyinen saldo: {{balance}} €',
    },
    preview: {
      button: 'Esikatsele haastattelua',
      banner: 'Tämä on esikatselu — krediittejä ei veloiteta.',
      loading: 'Luodaan esikatselu...',
      error: 'Esikatselua ei voitu käynnistää. Yritä uudelleen.',
      close: 'Sulje',
      completed: 'Esikatselu päättyi.',
      screener: {
        title: 'Esikarsintatiedot',
        continue: 'Jatka',
      },
      additionalInfo: {
        title: 'Lisätiedot',
        required: '*',
        continue: 'Jatka',
      },
      briefing: {
        aboutToStart: 'Olet aloittamassa tekoälyjohteisen videohaastattelun.',
        expectedDuration: 'Arvioitu kesto: noin {{minutes}} minuuttia.',
        quietEnvironment:
          'Varmista, että olet hiljaisessa ympäristössä toimivan mikrofonin kanssa.',
        consentText: 'Suostun nauhoittamiseen tämän haastattelun aikana.',
        termsOfService: 'Käyttöehdot',
        privacyPolicy: 'Tietosuojakäytäntö',
        starting: 'Käynnistetään...',
        ready: 'Olen valmis',
      },
      disqualified: {
        title: 'Kiitos kiinnostuksestasi',
        body: 'Valitettavasti et täytä tämän työpaikan vaatimuksia.',
      },
      room: {
        connecting: 'Haastattelija yhdistää...',
      },
    },
    voice: {
      loading: 'Ladataan ääniä...',
      noResults: 'Mikään ääni ei vastaa suodattimiasi.',
      filterLanguage: 'Kieli',
      filterAccent: 'Aksentti',
      filterCategory: 'Kategoria',
      filterGender: 'Sukupuoli',
      filterAge: 'Ikä',
    },
    screener: {
      questionPlaceholder: 'Esim. Onko sinulla voimassa oleva ajokortti?',
      optionPlaceholder: 'Vaihtoehto',
      singleSelect: 'Yksittäisvalinta',
      multiChoice: 'Monivalinta',
      markDisqualifying: 'Merkitse hylkääväksi',
      disqualifyingHint:
        'Rastita vaihtoehto hylätäksesi hakijat, jotka valitsevat sen.',
      addOption: 'Lisää vaihtoehto',
      removeOption: 'Poista vaihtoehto',
      addQuestion: 'Lisää kysymys',
      remove: 'Poista',
      save: 'Tallenna esikarsinta',
      saving: 'Tallennetaan...',
      saved: 'Tallennettu',
      empty: 'Ei esikarsintatiedot vielä. Lisää ensimmäinen kysymyksesi alle.',
      drag: 'Vedä muuttaaksesi järjestystä',
    },
    additionalInfo: {
      labelPlaceholder: 'Esim. Portfolio-URL',
      placeholderPlaceholder: 'Esim. https://...',
      typeText: 'Teksti',
      typeNumber: 'Numero',
      typeDate: 'Päivämäärä',
      typeUrl: 'URL',
      typeEmail: 'Sähköposti',
      requiredLabel: 'Pakollinen',
      addField: 'Lisää kenttä',
      remove: 'Poista',
      save: 'Tallenna kentät',
      saving: 'Tallennetaan...',
      saved: 'Tallennettu',
      empty: 'Ei kenttiä vielä. Lisää ensimmäinen kenttäsi alle.',
      drag: 'Vedä muuttaaksesi järjestystä',
    },
    questions: {
      textPlaceholder: 'Kirjoita haastattelukysymyksesi...',
      competencyPlaceholder: 'Osaaminen (valinnainen)',
      followUpPlaceholder: 'Jatkokysymys...',
      addQuestion: 'Lisää kysymys',
      addFollowUp: 'Lisää jatkokysymys',
      showFollowUps: 'Jatkokysymykset ({{count}})',
      hideFollowUps: 'Piilota jatkokysymykset',
      remove: 'Poista',
      removeFollowUp: 'Poista jatkokysymys',
      saveToBank: 'Tallenna pankkiin',
      addFromBank: 'Lisää pankista',
      save: 'Tallenna kysymykset',
      saving: 'Tallennetaan...',
      saved: 'Tallennettu',
      empty: 'Ei kysymyksiä vielä. Lisää ensimmäinen kysymyksesi alle.',
      drag: 'Vedä muuttaaksesi järjestystä',
    },
    locked: {
      notice:
        'Tätä ei voi muuttaa kun työpaikka on aktiivinen. Aseta työpaikka tauolle tehdäksesi muutoksia.',
    },
    generate: {
      title: 'Luo tekoälyllä',
      tabText: 'Liitä teksti',
      tabUrl: 'URL',
      tabPdf: 'PDF',
      textPlaceholder: 'Liitä työpaikkailmoitus tähän...',
      urlPlaceholder: 'https://example.com/tyopaikka',
      pdfLabel: 'Valitse PDF',
      pdfSelected: '{{name}}',
      generate: 'Luo',
      generating: 'Luodaan...',
      addToJob: 'Lisää',
      discard: 'Hylkää',
      addAll: 'Lisää kaikki',
      discardAll: 'Hylkää kaikki',
      error: 'Kysymyksiä ei voitu luoda. Yritä uudelleen.',
    },
    settings: {
      reminderCadenceTitle: 'Muistutusaikataulu',
      reminderCadenceDescription:
        'Määritä mitkä päivät kutsun jälkeen muistutus lähetetään. Jätä tyhjäksi käyttääksesi oletusaikataulua (puolivälissä, päivää ennen, viimeinen päivä).',
      reminderCadenceDefault:
        'Oletusaikataulua käytetään (puolivälissä, päivää ennen, viimeinen päivä).',
      reminderDay: 'Päivä {{day}}',
      addReminderDay: 'Lisää päivä',
      removeReminderDay: 'Poista muistutuspäivä',
      reminderDayPlaceholder: 'Päivä (esim. 2)',
      competencyWeightsTitle: 'Osaamispainot',
      competencyWeightsDescription:
        'Määritä suhteellinen paino kullekin osaamiselle keskipistemäärää laskettaessa. Oletuspaino on 1 (tasainen painotus).',
      competencyWeightsNoCompetencies:
        'Ei vielä määritettyjä osaamisia. Lisää ensin osaamisia haastattelukysymyksiisi.',
      competencyWeightsLocked:
        'Painoja ei voi muuttaa kun työpaikka on aktiivinen.',
      introMessageTitle: 'Johdantokuvaus',
      introMessageDescription:
        'Näytetään hakijoille briefing-näytöllä ennen haastattelun alkua. Jätä tyhjäksi käyttääksesi oletusviestiä.',
      introMessagePlaceholder: 'Lisää henkilökohtainen johdantoviesti…',
      outroMessageTitle: 'Loppukuvaus',
      outroMessageDescription:
        'Näytetään hakijoille loppunäytöllä haastattelun päätyttyä. Jätä tyhjäksi käyttääksesi oletusviestiä.',
      outroMessagePlaceholder: 'Lisää henkilökohtainen loppuviesti…',
      translationsTitle: 'Käännökset',
      translationsDescription:
        'Anna työn sisältö kaikilla haastattelukielillä. Käytä käännöspainiketta luonnosten luomiseen, tarkista ja tallenna ne sitten.',
      candidateFeedbackTitle: 'Hakijapalaute',
      candidateFeedbackDescription:
        'Näytä hakijoille heidän henkilökohtaiset osaamispisteensä haastattelun jälkeen.',
      candidateFeedbackEnabled: 'Näytä palaute hakijoille',
    },
    translations: {
      generating: 'Generoidaan…',
      translateFrom: 'Käännä kielestä {{language}}',
      draftHint:
        'Tekoälyluonnos — tarkista ja muokkaa, napsauta sitten ulkopuolelle tallentaaksesi.',
      swedish: 'Ruotsi',
      english: 'Englanti',
    },
  },
  questionBank: {
    addQuestion: 'Lisää kysymys',
    textPlaceholder: 'Kirjoita haastattelukysymyksesi...',
    competencyPlaceholder: 'Osaaminen (valinnainen)',
    save: 'Tallenna',
    empty: 'Ei kysymyksiä vielä. Lisää ensimmäinen kysymyksesi alle.',
    delete: 'Poista',
    deleteConfirm: 'Oletko varma?',
  },
  interviews: {
    header: {
      title: 'Haastatteluraportti',
      description:
        'Tarkastele hakijan haastattelutuloksia ja tekoälyanalyysiä.',
    },
    candidates: {
      noInterviews: 'Ei haastatteluja vielä.',
      statusPending: 'Odottaa',
      statusInProgress: 'Käynnissä',
      statusCompleted: 'Valmis',
      statusFailed: 'Epäonnistui',
      viewReport: 'Näytä raportti',
      duration: '{{min}}m {{sec}}s',
      capacity: '{{used}} / {{total}} paikkaa käytetty',
      increaseCapacity: 'Lisää kapasiteettia',
      increaseCapacityTitle: 'Lisää haastattelukapasiteettia',
      increaseCapacityDescription:
        'Jokainen lisäpaikka maksaa {{cost}} krediittiä. Nykyinen saldosi on {{balance}} krediittiä.',
      additionalSlots: 'Lisäpaikat',
      increaseCapacityConfirm: 'Vahvista',
      increaseCapacityCancel: 'Peruuta',
      increaseCapacityInsufficientBalance:
        'Riittämätön saldo. Tarvitset {{required}} krediittiä mutta sinulla on vain {{balance}}.',
    },
    candidateActions: {
      calendlyUrlLabel: 'Calendly-linkki',
      calendlyUrlDescription:
        'Liitä Calendly-linkkisi lähettääksesi sen etenevien hakijoiden.',
      saveCalendlyUrl: 'Tallenna',
      noCandidates: 'Ei hakijoita vielä.',
      statusInvited: 'Kutsuttu',
      statusStarted: 'Aloitettu',
      statusCompleted: 'Valmis',
      statusExpired: 'Vanhentunut',
      statusDisqualified: 'Hylätty',
      statusRejected: 'Kieltäytynyt',
      statusAdvanced: 'Edennyt',
      reject: 'Hylkää',
      advance: 'Etene',
      reTake: 'Salli uusintayritys',
      rejectDialogTitle: 'Lähetä hylkäyssähköposti',
      rejectDialogDescription:
        'Tämä lähettää hylkäyssähköpostin hakijalle. Voit lisätä valinnaisen henkilökohtaisen viestin alle.',
      rejectCustomMessagePlaceholder: 'Valinnainen henkilökohtainen viesti...',
      rejectConfirm: 'Lähetä hylkäys',
      advanceConfirmTitle: 'Lähetä hakija eteenpäin',
      advanceConfirmDescription:
        'Tämä lähettää Calendly-linkin hakijalle haastattelun varaamiseen.',
      advanceConfirm: 'Etene',
      reTakeConfirmTitle: 'Salli uusintayritys',
      reTakeConfirmDescription:
        'Tämä nollaa hakijan kutsulinkin ja pidentää määräaikaa. Hakija saa uuden linkin.',
      reTakeConfirm: 'Salli uusintayritys',
      cancel: 'Peruuta',
      selected: '{{count}} valittu',
      bulkReject: 'Hylkää valitut',
      bulkAdvance: 'Etene valituille',
      bulkSendReminder: 'Lähetä muistutus',
      bulkRejectDialogTitle: 'Hylkää valitut hakijat',
      bulkRejectDialogDescription:
        'Tämä lähettää hylkäyssähköpostin {{count}} hakijalle. Voit lisätä valinnaisen henkilökohtaisen viestin alle.',
      bulkRejectConfirm: 'Hylkää kaikki',
    },
    report: {
      back: 'Takaisin työpaikkaan',
      summary: 'Yhteenveto',
      noSummary: 'Analyysi käynnissä...',
      competencies: 'Osaamisarviointi',
      noScores: 'Ei pisteitä vielä.',
      rationale: 'Perustelu',
      quotes: 'Lainaukset',
      noQuotes: 'Ei lainauksia.',
      transcript: 'Transkripti',
      noTranscript: 'Ei transkriptia saatavilla.',
      duration: 'Kesto',
      completedAt: 'Valmistui',
      shareLabel: 'Jaa raportti',
      shareEnabled: 'Jakaminen käytössä',
      shareDisabled: 'Jakaminen poistettu käytöstä',
      copyLink: 'Kopioi linkki',
      linkCopied: 'Kopioitu!',
      video: 'Videotallenne',
      noVideo: 'Ei videotallennetta saatavilla.',
      videoLoading: 'Ladataan videota...',
      clips: 'Osaamisleiket',
      noClips: 'Ei leikkeitä saatavilla vielä.',
      clipDuration: '{{sec}}s',
      reviewedBy: 'Tarkistanut',
      noReviewers: 'Kukaan ei ole vielä tarkistanut tätä raporttia.',
      notes: 'Muistiinpanot',
      noNotes: 'Ei muistiinpanoja vielä.',
      addNote: 'Lisää muistiinpano',
      notePlaceholder: 'Kirjoita muistiinpano…',
      deleteNote: 'Poista',
      noteAdded: 'Muistiinpano lisätty.',
      aiDetection: 'Tekoälyvastaustunnistus',
      aiDetectionPending: 'Analyysi käynnissä...',
      aiDetectionLow: 'Vastaukset vaikuttavat ihmisiltä',
      aiDetectionMedium: 'Joitakin tekoälyn kaltaisia malleja löydetty',
      aiDetectionHigh: 'Vastaukset todennäköisesti tekoälyn tuottamia',
      aiDetectionScore: 'Todennäköisyyspisteet',
      aiDetectionFlags: 'Merkityt segmentit',
    },
    chat: {
      title: 'Kysy hakijasta',
      placeholder: 'Esitä kysymys hakijan vastauksista haastattelussa...',
      inputPlaceholder: 'Kirjoita kysymys...',
      send: 'Lähetä',
      sending: 'Lähetetään...',
      errorMessage: 'Tapahtui virhe. Yritä uudelleen.',
    },
    orgChat: {
      title: 'Kysy hakijoista',
      placeholder:
        'Esitä kysymys mistä tahansa hakijasta kaikissa työpaikoissa...',
      inputPlaceholder:
        'Esim. Kuka sai korkeimmat pisteet ongelmanratkaisussa?',
      send: 'Lähetä',
      sending: 'Lähetetään...',
      errorMessage: 'Tapahtui virhe. Yritä uudelleen.',
      toggle: 'Tekoälychatti',
    },
    publicReport: {
      notFound: 'Tämä raportti ei ole enää saatavilla.',
      notFoundDescription:
        'Linkki on saattanut vanhentua tai jakaminen on poistettu käytöstä.',
    },
    pdfExport: {
      downloadButton: 'Lataa',
      aggregatedReport: 'Lataa koostettu raportti',
      transcripts: 'Lataa transkriptit',
      individualReports: 'Lataa yksittäiset raportit',
      drawerTitleTranscripts: 'Lataa transkriptit',
      drawerTitleReports: 'Lataa yksittäiset raportit',
      drawerDescription: 'Valitse lataukseen sisällytettävät ehdokkaat.',
      selectAll: 'Valitse kaikki',
      deselectAll: 'Poista valinnat',
      downloadSelected: 'Lataa valitut',
      downloadAll: 'Lataa kaikki',
      generating: 'Luodaan {{completed}} / {{total}}...',
      complete: 'Lataus valmis',
      error: 'Vienti epäonnistui. Yritä uudelleen.',
      noCompletedCandidates: 'Ei suoritettuja haastatteluja vietäväksi.',
      cancel: 'Peruuta',
    },
    aggregateReport: {
      noCompletedInterviews: 'Ei valmiita haastatteluja vielä.',
      rank: 'Sijoitus',
      candidate: 'Hakija',
      averageScore: 'Keskipisteet',
      topN: 'Top {{n}}',
      competencies: 'Osaamisjakauma',
    },
    candidateFeedback: {
      title: 'Tuloksesi',
      preparing: 'Henkilökohtaista palautettasi valmistellaan…',
      competencies: 'Osaamispisteet',
      scoreLabel: '{{score}} / 10',
    },
  },
  candidateSearch: {
    searchPlaceholder: 'Hae nimellä tai sähköpostilla…',
    filterJob: 'Kaikki työpaikat',
    filterStatus: 'Kaikki tilat',
    filterMinScore: 'Vähimmäispisteet',
    filterMaxScore: 'Enimmäispisteet',
    filterFromDate: 'Alkaen',
    filterToDate: 'Päättyen',
    clearFilters: 'Tyhjennä suodattimet',
    noResults: 'Hakijoita ei löydetty.',
    columnName: 'Nimi',
    columnJob: 'Työpaikka',
    columnStatus: 'Tila',
    columnScore: 'Keskipisteet',
    columnDate: 'Kutsuttu',
    viewReport: 'Näytä raportti',
    scoreValue: '{{score}}',
    noScore: '—',
  },
  notifications: {
    title: 'Ilmoitukset',
    markAllRead: 'Merkitse kaikki luetuksi',
    empty: 'Ei ilmoituksia vielä.',
    typeInterviewCompleted: 'Haastattelu valmis',
    typeInterviewFailed: 'Haastattelu epäonnistui',
    typeLowBalance: 'Matala saldo',
    typeJobActivated: 'Työpaikka aktivoitu',
    typeCandidateExpired: 'Hakija vanhentunut',
  },
  dashboard: {
    getStarted: 'Aloita',
    getStartedDescription:
      'Tutustu kojelautaan ja aloita ensimmäisellä sovelluksellasi.',
    tipChangeLanguage: 'VINKKI: Kokeile kielen vaihtamista!',
    exploreDocumentation: 'Tutustu dokumentaatioon',
    statsInvited: 'Kutsutut',
    statsCompleted: 'Valmiit',
    statsAdvanced: 'Edenneet',
    statsAvgScore: 'Keskimääräinen pistemäärä',
    analytics: {
      title: 'Analytiikka',
      jobTitle: 'Työpaikka',
      status: 'Tila',
      completionRate: 'Valmistumisaste',
    },
    accountLocked: {
      message:
        'Tilauksesi on vanhentunut. Aktivoi uudelleen jatkaaksesi haastattelujen pitämistä.',
      reactivate: 'Aktivoi uudelleen',
    },
  },
  settings: {
    avatar: {
      title: 'Avatarimasi',
      description: 'Tämä on avatarimasi. Se näytetään profiilissasi.',
      hint: 'Napsauta avataria ladataksesi mukautetun tiedostoistasi.',
      reset: 'Nollaa',
    },
    profile: {
      title: 'Profiilisi',
      description: 'Etu- ja sukunimesi näytetään hakijoille ja kollegoille.',
      firstNamePlaceholder: 'Etunimi',
      lastNamePlaceholder: 'Sukunimi',
      hint: 'Käytä oikeaa nimeäsi.',
      save: 'Tallenna',
    },
    username: {
      title: 'Käyttäjänimesi',
      description: 'Tämä on käyttäjänimesi. Se näytetään profiilissasi.',
      placeholder: 'Käyttäjänimi',
      hint: 'Käytä enintään 32 merkkiä.',
      save: 'Tallenna',
    },
    deleteAccount: {
      title: 'Poista tili',
      description:
        'Poista tilisi pysyvästi, kaikki projektisi ja kaikki niihin liittyvät tiedot.',
      hint: 'Tätä toimintoa ei voi kumota, jatka varoen.',
      confirm: 'Oletko varma?',
      delete: 'Poista tili',
    },
    contactSupport: {
      title: 'Ota yhteyttä tukeen',
      description: 'Ilmoita virheestä, esitä kysymys tai pyydä ominaisuutta.',
      topicLabel: 'Aihe',
      topicBug: 'Virhe',
      topicTechnicalIssue: 'Tekninen ongelma',
      topicBilling: 'Laskutus',
      topicGeneral: 'Yleinen kysymys',
      topicFeatureRequest: 'Ominaisuuspyyntö',
      severityLabel: 'Vakavuus',
      severityLow: 'Matala',
      severityMedium: 'Keskitaso',
      severityHigh: 'Korkea',
      severityCritical: 'Kriittinen',
      subjectLabel: 'Aihe',
      subjectPlaceholder: 'Lyhyt kuvaus ongelmasta',
      messageLabel: 'Viesti',
      messagePlaceholder: 'Kuvaile ongelmasi yksityiskohtaisesti…',
      submit: 'Lähetä',
      submitting: 'Lähetetään…',
      successTitle: 'Pyyntö vastaanotettu',
      successMessage: 'Tukipyyntösi on lähetetty. Viite: {{ticketId}}',
      hint: 'Palaamme asiaan mahdollisimman pian.',
    },
    tabs: {
      general: 'Yleinen',
      billing: 'Laskutus',
      organisation: 'Organisaatio',
      branding: 'Brändi',
      team: 'Tiimi',
      notifications: 'Ilmoitukset',
    },
    branding: {
      title: 'Brändi',
      description: 'Mukauta miten organisaatiosi näytetään hakijoille.',
      logoTitle: 'Logo',
      logoDescription: 'Näytetään hakijan haastattelutietonäytöllä.',
      uploadLogo: 'Lataa logo',
      uploading: 'Ladataan...',
      primaryColorTitle: 'Pääväri',
      primaryColorDescription:
        'Käytetään painikkeissa ja aksenteissa hakijavirrassa.',
      websiteTitle: 'Verkkosivusto',
      websiteDescription:
        'Näytetään hakijoille linkkinä yrityksesi tutustumiseen.',
      taglineTitle: 'Iskulause',
      taglineDescription:
        'Lyhyt kuvaus yrityksestäsi, joka näytetään tietonäytöllä.',
      taglinePlaceholder: 'Esim. Rakennamme tulevaisuuden työtä',
      hint: 'Nämä asetukset ovat hakijoiden nähtävissä.',
      save: 'Tallenna',
      saving: 'Tallennetaan...',
      saved: 'Tallennettu',
    },
    notifications: {
      title: 'Ilmoitusasetukset',
      description: 'Valitse mitkä sähköposti-ilmoitukset haluat vastaanottaa.',
      lowBalanceLabel: 'Matala saldo',
      lowBalanceDescription:
        'Saat sähköpostin kun saldosi laskee 5:teen tai alle haastatteluun.',
      interviewCompletedLabel: 'Haastattelu valmis',
      interviewCompletedDescription:
        'Saat sähköpostin kun hakija suorittaa haastattelunsa.',
      reportReadyLabel: 'Raportti valmis',
      reportReadyDescription:
        'Saat sähköpostin kun haastattelun tekoälyanalyysi on valmis.',
      teamMemberJoinedLabel: 'Tiimin jäsen liittyi',
      teamMemberJoinedDescription:
        'Saat sähköpostin kun uusi tiimin jäsen liittyy organisaatioosi.',
    },
    team: {
      title: 'Tiimin jäsenet',
      description: 'Hallinnoi tiimisi pääsyä organisaatioon.',
      inviteTitle: 'Kutsu tiimin jäsen',
      inviteDescription: 'Lähetä kutsu sähköpostitse.',
      emailLabel: 'Sähköposti',
      emailPlaceholder: 'kollega@esimerkki.fi',
      roleLabel: 'Rooli',
      roleMember: 'Jäsen',
      roleAdmin: 'Järjestelmänvalvoja',
      roleOwner: 'Omistaja',
      invite: 'Kutsu',
      inviteSent: 'Kutsu lähetetty.',
      membersTitle: 'Nykyiset jäsenet',
      remove: 'Poista',
      removeDialogTitle: 'Poista tiimin jäsen',
      removeDialogDescription:
        'Haluatko varmasti poistaa {{name}} organisaatiosta?',
      removeConfirm: 'Poista',
      cancel: 'Peruuta',
      hint: 'Kutsutut käyttäjät saavat pääsyn organisaation resursseihin.',
    },
    organisation: {
      title: 'Organisaatioasetukset',
      description: 'Asetukset jotka näytetään hakijoille.',
      displayNameTitle: 'Näyttönimi',
      displayNameDescription:
        'Tämä nimi näytetään hakijoille haastatteluportaalissa.',
      displayNamePlaceholder: 'Organisaation nimi',
      termsUrlTitle: 'Käyttöehtojen linkki',
      termsUrlDescription: 'Näytetään hakijoille haastattelukutsun yhteydessä.',
      termsUrlPlaceholder: 'https://example.com/terms',
      privacyUrlTitle: 'Tietosuojakäytännön linkki',
      privacyUrlDescription:
        'Näytetään hakijoille haastattelukutsun yhteydessä.',
      privacyUrlPlaceholder: 'https://example.com/privacy',
      hint: 'Nämä asetukset näytetään hakijoille.',
      save: 'Tallenna',
      sso: {
        title: 'Kertakirjautuminen (SSO)',
        description:
          'Anna tiimin jäsenten kirjautua sisään organisaation identiteettipalveluntarjoajalla.',
        emailDomainTitle: 'Sähköpostitoimialue',
        emailDomainDescription:
          'SSO-käyttäjien tunnistamiseen käytettävä sähköpostitoimialue (esim. yritys.fi).',
        emailDomainPlaceholder: 'yritys.fi',
        discoveryUrlTitle: 'OIDC Discovery -URL',
        discoveryUrlDescription:
          'OpenID Connect -konfiguraation URL identiteettipalveluntarjoajallasi.',
        discoveryUrlPlaceholder:
          'https://idp.example.com/.well-known/openid-configuration',
        clientIdTitle: 'Asiakastunnus',
        clientIdDescription:
          'Identiteettipalveluntarjoajalla rekisteröity asiakastunnus.',
        clientIdPlaceholder: 'asiakastunnus',
        clientSecretTitle: 'Asiakassalaisuus',
        clientSecretDescription:
          'Identiteettipalveluntarjoajalla rekisteröity asiakassalaisuus.',
        clientSecretPlaceholder: '••••••••',
        clientSecretSetOn: 'Asetettu {{date}}',
        clientSecretRotate: 'Kierrä',
        clientSecretRemove: 'Poista',
        saveConfig: 'Tallenna SSO-konfiguraatio',
        enforceTitle: 'Vaadi SSO',
        enforceDescription:
          'Vaadi tiimin jäseniä kirjautumaan SSO:lla. Organisaation omistajat ovat aina poikkeuksena.',
        enforced: 'Käytössä',
        notEnforced: 'Ei käytössä',
        hint: 'SSO-konfiguraatio näytetään vain organisaation omistajille.',
      },
    },
  },
  support: {
    contactForm: {
      nameLabel: 'Nimi',
      namePlaceholder: 'Nimesi',
      emailLabel: 'Sähköposti',
      emailPlaceholder: 'sinun@sahkoposti.fi',
      successTitle: 'Viesti lähetetty',
      successMessage:
        'Olemme vastaanottaneet viestisi ja palaamme asiaan mahdollisimman pian.',
      errorGeneric: 'Jotain meni pieleen. Yritä uudelleen.',
    },
  },
  billing: {
    balanceTitle: 'Saldosi',
    balanceDescription: 'Käytettävissä oleva saldo haastatteluihin.',
    subscriptionTitle: 'Tilaus',
    subscriptionDescription:
      'Aloita 14 päivän ilmainen kokeilujakso. Ei maksua ennen kokeilujakson päättymistä.',
    subscriptionNote:
      'Sinut ohjataan Polariin rekisteröitymisen viimeistelyyn.',
    subscriptionStatusActive: 'Aktiivinen',
    subscriptionStatusTrialing: 'Kokeilujakso',
    subscribeLabel: 'Aloita ilmainen kokeilujakso',
    manageSubscription: 'Hallinnoi tilausta',
    trialEndsOn: 'Kokeilujakso päättyy {{date}}',
    topUpTitle: 'Lisää saldoa',
    topUpDescription: 'Valitse summa tilin saldosi lisäämiseen.',
    topUpNote: 'Sinut ohjataan Polariin maksamaan.',
    topUpLabel: 'Lisää',
    invoiceHistoryTitle: 'Laskuhistoria',
    invoiceHistoryDescription: 'Tarkastele aiempia ostoksiasi ja laskujasi.',
    redirectedToPolar: 'Sinut ohjataan Polar-asiakasportaaliin.',
    viewInvoices: 'Näytä laskut',
    billingEmailTitle: 'Laskutusosoite',
    billingEmailDescription:
      'Polarin kuitit ja laskut lähetetään tähän osoitteeseen.',
    billingEmailPlaceholder: 'lasku@esimerkki.fi',
    billingEmailSave: 'Tallenna',
    billingEmailSaved: 'Tallennettu.',
    billingEmailHint:
      'Jätä tyhjäksi käyttääksesi tilin sähköpostiosoitetta. Vain järjestelmänvalvojat ja omistajat voivat muuttaa tätä.',
    autoTopUpTitle: 'Automaattinen täydennys',
    autoTopUpDescription:
      'Täydennä saldosi automaattisesti kun se laskee alle kynnysarvon.',
    autoTopUpEnabled: 'Käytössä',
    autoTopUpDisabled: 'Poistettu käytöstä',
    autoTopUpNoPaymentMethod:
      'Ei tallennettua maksutapaa. Suorita ensin ostos aktivoidaksesi automaattisen täydennyksen.',
    autoTopUpThresholdLabel: 'Täydennä kun alle',
    autoTopUpThresholdUnit: 'haastattelua jäljellä',
    autoTopUpProductLabel: 'Täydennyspaketti',
    autoTopUpSave: 'Tallenna',
    autoTopUpSaved: 'Tallennettu.',
  },
  checkout: {
    title: 'Maksu suoritettu',
    description: 'Maksusi on suoritettu ja saldosi on päivitetty.',
    completed: 'Ostos suoritettu!',
    returnToDashboard: 'Palaa kojelautaan',
  },
  templates: {
    empty: 'Ei mallipohjia vielä',
    emptyDescription:
      'Tallenna olemassa oleva työ mallipohjana käyttääksesi sitä uudelleen.',
    questionCount: 'Kysymykset',
    screenerCount: 'Esikarsinta',
    interviewCount: 'Haastattelut',
    useTemplate: 'Käytä mallipohjaa',
    saveAsTemplate: 'Tallenna mallipohjana',
    saveAsTemplateTitle: 'Tallenna mallipohjana',
    templateNameLabel: 'Mallipohjan nimi',
    templateNamePlaceholder: 'Esim. Senior Backend-kehittäjä',
    saveButton: 'Tallenna',
    createFromTemplateTitle: 'Luo työpaikka mallista "{{name}}"',
    createJobButton: 'Luo työpaikka',
  },
  candidate: {
    loading: 'Ladataan…',
    invalid: {
      title: 'Linkki on virheellinen',
      body: 'Tämä kutsuslinkki on vanhentunut tai ei ole enää voimassa.',
    },
    orgLocked: {
      title: 'Tili on inaktiivinen',
      body: 'Tämän organisaation tili on tällä hetkellä inaktiivinen. Ota yhteyttä rekrytoijaan saadaksesi apua.',
    },
    disqualified: {
      title: 'Kiitos kiinnostuksestasi',
      body: 'Valitettavasti et täytä tämän työpaikan vaatimuksia.',
    },
    screener: {
      title: 'Esikarsintatiedot',
      continue: 'Jatka',
    },
    additionalInfo: {
      title: 'Lisätiedot',
      continue: 'Jatka',
    },
    briefing: {
      howToConduct: 'Miten haluat suorittaa haastattelun?',
      usesModality: 'Tämä haastattelu käyttää',
      aboutToStart: 'Olet aloittamassa tekoälyjohteista haastattelua.',
      expectedDuration: 'Arvioitu kesto: noin {{minutes}} minuuttia.',
      quietEnvironment:
        'Varmista, että olet hiljaisessa ympäristössä toimivan mikrofonin kanssa.',
      aiNotice:
        'Vastauksesi arvioidaan tekoälyjärjestelmällä. Ihmisrekrytoija tarkistaa kaikki tekoälyn tuottamat tulokset ja tekee jokaisen rekrytointipäätöksen itsenäisesti – hakemuksestasi ei tehdä automaattisia päätöksiä.',
      requestScores:
        'Voit milloin tahansa pyytää osaamispisteesi ja perustelusi sähköpostitse osoitteesta',
      consentRecording: 'Suostun nauhoittamiseen tämän haastattelun aikana.',
      consentAi:
        'Suostun siihen, että vastauksiani käsitellään tekoälyllä tämän haastattelun aikana.',
      termsOfService: 'Käyttöehdot',
      privacyPolicy: 'Tietosuojakäytäntö',
      starting: 'Käynnistetään…',
      ready: 'Olen valmis',
      insufficientBalance:
        'Haastattelua ei voitu käynnistää juuri nyt. Ota yhteyttä rekrytoijaan.',
      orgLockedError:
        'Tämän organisaation tili on tällä hetkellä inaktiivinen. Ota yhteyttä rekrytoijaan.',
    },
    modality: {
      video: { label: 'Video', description: 'Kamera ja mikrofoni' },
      audio: { label: 'Vain ääni', description: 'Mikrofoni' },
      chat: { label: 'Teksti', description: 'Kirjalliset vastaukset' },
    },
    room: {
      connecting: 'Haastattelija yhdistää…',
      questionOf: 'Kysymys {{index}} / {{total}}',
      answered: '{{completed}} / {{total}} vastattu',
      chatPlaceholder: 'Kirjoita vastauksesi…',
      chatSend: 'Lähetä',
      chatLeave: 'Poistu',
    },
    completed: {
      title: 'Haastattelu on valmis',
      defaultOutro: 'Kiitos ajastasi. Palaamme pian.',
      feedbackQuestion: 'Millainen kokemuksesi oli?',
      feedbackComment: 'Lisäkommentteja? (valinnainen)',
      feedbackSubmit: 'Lähetä palaute',
      feedbackThanks: 'Kiitos palautteestasi.',
    },
    abandoned: {
      title: 'Haastattelu keskeytyi',
      body: 'Olet poistunut haastattelusta. Jos se oli tarkoituksellista, sinun ei tarvitse tehdä mitään muuta.',
    },
    error: {
      title: 'Jotain meni pieleen',
      body: 'Haastattelusi aikana tapahtui odottamaton virhe. Yritä uudelleen tai ota yhteyttä tukeen.',
      reportLink: 'Ilmoita teknisestä ongelmasta',
      severityTitle: 'Kuinka vakava ongelma oli?',
      severityLow: 'Pieni – pystyin jatkamaan',
      severityMedium: 'Merkittävä – haastattelu häiriintyi',
      severityHigh:
        'Kriittinen – en pystynyt suorittamaan haastattelua loppuun',
      describeTitle: 'Kuvaile mitä tapahtui',
      describePlaceholder: 'Esim. Ääni lakkasi toimimasta, sivu jumiutui…',
      submit: 'Lähetä raportti',
      submitting: 'Lähetetään…',
      submitted: 'Raporttisi on lähetetty. Kiitos.',
    },
  },
  ai: {
    generate: 'Luo',
  },
}

export default fi
